import { z } from 'zod'
import type { ClientKind } from '@/kinds/kind'
import { conflict, invalid } from '@/kinds/kind'
import type { Resource } from '@/db/types'
import { formatTime } from '@/lib/time'

/**
 * Mirrors agentsession.Image: `blob` (an id from POST /api/blobs) or `data` (base64), never both. A
 * stored picture may carry neither (a build from before blobs rewrote the thread without the field);
 * the thread still takes writes and the picture is named as gone. A new message carries one (CheckImages).
 */
export const agentPicture = z
  .object({
    media_type: z.string(),
    data: z.string().optional(),
    blob: z
      .string()
      .regex(/^[0-9a-f]{64}$/)
      .optional(),
  })
  .refine((p) => p.data === undefined || p.blob === undefined, {
    message: 'a picture is a blob or inline data, not both',
  })
export type AgentPicture = z.infer<typeof agentPicture>

export const agentMessage = z.object({
  role: z.enum(['user', 'assistant', 'tool']),
  content: z.string().max(20000),
  at: z.string(),
  suggestions: z.array(z.string()).optional(),
  /** text (a keyboard) or voice (the spoken agent); both land on the same thread */
  channel: z.enum(['text', 'voice']).optional(),
  /** device tools the assistant asked this device to run at the end of the turn */
  tool_calls: z.array(z.object({ id: z.string(), name: z.string(), args: z.unknown() })).optional(),
  /** server tools the turn ran (agents, search…); the history tells the brain so */
  looked: z.array(z.string()).optional(),
  /** news about a coding agent (its AgentRun id): the factory telling Prime it finished or failed */
  agent_run: z.string().optional(),
  /** for a tool message: which call it answers */
  tool_call_id: z.string().optional(),
  /** a user message that is something to file, framed to the brain as content, never instructions */
  capture: z.boolean().optional(),
  /** who sent a shared capture (the grant's label); absent means the owner */
  from: z.string().optional(),
  /** the inbox task a server-side capture already made, which the turn files */
  carrier: z.string().optional(),
  /** older messages: the page open in the desktop panel when it showed the workspace's Prime thread */
  page: z.string().optional(),
  /** pictures attached to a typed message: a blob id each, or base64 inline (no data: prefix) from older clients */
  images: z.array(agentPicture).max(4).optional(),
  /** the buttons under a member's Prime question (a tap sends the words back) */
  options: z.array(z.string().max(80)).max(4).optional(),
  /** pages a member's Prime showed beside her thread */
  pages: z.array(z.string().max(64)).max(8).optional(),
  /** what Prime put on the side panel's canvas in this reply */
  canvas: z
    .object({ kind: z.enum(['mermaid', 'svg', 'html']), title: z.string().optional(), content: z.string() })
    .optional(),
})
export type AgentMessage = z.infer<typeof agentMessage>

export const agentSessionSpec = z
  .object({
    title: z.string().min(1).max(120),
    /** bound to a page: its text rides along every turn */
    page_id: z.string().optional(),
    /** the one long-lived thread per workspace that voice and text share */
    prime: z.boolean().optional(),
    model: z.string().optional(),
    messages: z.array(agentMessage).max(200),
  })
  .strict()
export type AgentSessionSpec = z.infer<typeof agentSessionSpec>

export const agentSessionStatus = z.object({
  state: z.enum(['active', 'archived']),
  message_count: z.number().int().default(0),
  last_at: z.string().optional(),
  busy: z.boolean().optional(),
  /** what the agent is doing right now, shown while busy */
  step: z.string().optional(),
  last_error: z.string().optional(),
})
export type AgentSessionStatus = z.infer<typeof agentSessionStatus>
export type AgentSessionResource = Resource<AgentSessionSpec, AgentSessionStatus>

/** Mirrors internal/kinds/agentsession Apply. */
export const apply = (
  r: AgentSessionResource,
  action: string,
  args: unknown,
  at: string,
): AgentSessionResource => {
  const st: AgentSessionStatus = { ...r.status }
  let spec = r.spec
  const clocks = { ...r.clocks }
  switch (action) {
    case 'archive':
      if (st.state === 'archived') throw conflict('session is already archived')
      st.state = 'archived'
      break
    case 'restore':
      if (st.state !== 'archived') throw conflict('session is not archived')
      st.state = 'active'
      break
    case 'rename': {
      const title = String((args as { title?: unknown } | null)?.title ?? '').trim()
      if (!title) throw invalid('rename: title is required')
      spec = { ...spec, title }
      clocks['spec.title'] = formatTime(new Date(at))
      break
    }
    default:
      throw invalid(`unknown action ${action}`)
  }
  return { ...r, spec, status: st, clocks }
}

const Nothing = () => null

export const agentSessionKind: ClientKind<AgentSessionSpec, AgentSessionStatus> = {
  name: 'AgentSession',
  version: 1,
  spec: agentSessionSpec,
  status: agentSessionStatus,
  defaults: (spec) => ({ title: 'New session', messages: [], ...spec }) as AgentSessionSpec,
  validate: () => [],
  initialStatus: (r) => ({ state: 'active', message_count: r.spec.messages.length }),
  displayName: (r) => r.spec.title,
  apply,
  actions: ['archive', 'restore', 'rename'],
  ListRow: Nothing,
  Detail: Nothing,
  Form: Nothing,
  workspaceScoped: true,
}
